import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RoleInfo } from '../../models/user/role-info';
import { IAddress } from '../../models/user/address';
import { UserInfo } from '../../models/user/user-info';
import { IUserResetPassword } from 'src/app/models/user/user-credentials';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient) { }

  login(credentials: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/sign-in`;
    return this.http.post(url, credentials);
  }

  resetPassword(newDentials: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/sign-in`;
    return this.http.post(url, newDentials);
  }

  changePassword(changePasswordDetials: IUserResetPassword): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/change-password`;
    return this.http.post(url, changePasswordDetials);
  }

  saveRole(role: RoleInfo): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/roles`;
    if (role._id == null) {
      return this.http.put(url, role);
    } else {
      return this.http.post(url, role);
    }
  }

  saveUser(user: UserInfo): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users`;
    if (user._id == null) {
      return this.http.put(url, user);
    } else {
      return this.http.post(url, user);
    }
  }

  getEscalationCandidates(userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/escalation-candidates/${userId}`;
    return this.http.get(url);
  }

  getRoles(): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/roles`;
    return this.http.get(url);
  }

  getRole(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/roles/${id}`;
    return this.http.get(url);
  }

  deleteRole(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/roles/${id}`;
    return this.http.delete(url);
  }

  getCustomerIdentity(mobileNumber: string): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users/identity/${mobileNumber}`;
    return this.http.get(url);
  }

  getUserProfile(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users/${id}`;
    return this.http.get(url);
  }

  deleteUser(id: string): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users/${id}`;
    return this.http.delete(url);
  }

  getCustomers(searchText: string): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users`;

    return this.http.post(url, { searchText });
  }

  getUser(searchText: string): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/user/search`;
    const criteria: any = {};
    criteria.searchText = searchText;

    return this.http.post(url, criteria);
  }

  getSignInHistory(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/sign-in-history/${id}`;
    return this.http.get(url, id);
  }

  saveTag(id: any, tags: any): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/customer-tags`;
    const customerTags: any = {};
    customerTags.id = id;
    customerTags.tags = tags;
    return this.http.post(url, customerTags);
  }

  getCustomerTags() {
    const url = `${environment.apiOrigin}/UserService/api/customer-tags`;
    this.http.get(url);
  }

  updateUser(customerId: string, field: string, value: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/field`;
    const updateDefinition: any = {};
    updateDefinition.userId = customerId;
    updateDefinition.field = field;
    updateDefinition.value = value;
    return this.http.post(url, updateDefinition);
  }

  updateUserAddressList(id: any, addressList: IAddress[]): Observable<any> {
    const updateUserAddressDefinition: any = {};
    updateUserAddressDefinition.userId = id;
    updateUserAddressDefinition.addressList = addressList;
    const url = `${environment.apiOrigin}/UserService/api/user/address-list`;
    return this.http.post(url, updateUserAddressDefinition);
  }

  saveApp(id: any, apps: any): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/customer-apps`;
    const customerApps: any = {};
    customerApps.id = id;
    customerApps.apps = apps;
    return this.http.post(url, customerApps);
  }

  updateRoles(id: any, rolesInfo: RoleInfo[]): Observable<any> {
    const roleDefinition: any = {};
    roleDefinition.userId = id;
    roleDefinition.rolesInfo = rolesInfo;
    const url = `${environment.apiOrigin}/api/users/roles`;
    return this.http.post(url, roleDefinition);
  }

  getEmployees(): Observable<any> {
    const url = `${environment.apiOrigin}/UserService/api/users/employees`;
    return this.http.get(url);
  }

  getUserById(userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/${userId}`;
    return this.http.get(url);
  }

  getUsers(): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/company-users-list`;
    return this.http.get(url);
  }

  requestOTP(username: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/otp`;
    const userDetail: any = {};
    userDetail.username = username;
    userDetail.app = 'converge';
    return this.http.post(url, userDetail);
  }

  verifyOtp(username: string, otp: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/verify-otp`;
    const otpDetails: any = {};
    otpDetails.username = username;
    otpDetails.otp = otp;
    return this.http.post(url, otpDetails);
  }

  verifyMfa(mfaToken: string, otp: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/verify-mfa`;
    const request = { MfaToken: mfaToken, Otp: otp };
    return this.http.post(url, request);
  }

  resendMfaOtp(mfaToken: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/resend-mfa-otp`;
    const request = { mfaToken: mfaToken };
    return this.http.post(url, request);
  }

  updatePassword(username: string, password: string, otp: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/password`;
    const updatePasswordForm: any = {};
    updatePasswordForm.username = username;
    updatePasswordForm.password = password;
    updatePasswordForm.otp = otp;
    updatePasswordForm.app = 'converge';
    return this.http.post(url, updatePasswordForm);
  }

  createAdminUser(value: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/create-admin-user`;
    return this.http.put(url, value);
  }

  getUserDetailsByPhoneOrEmail(searchText: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/search`;
    const criteria: any = {};
    criteria.searchText = searchText;
    return this.http.post(url, criteria);
  }

  searchUser(searchText: any): Observable<any> {
    const criteria: any = {};
    criteria.searchText = searchText;
    const url = `${environment.apiOrigin}/api/users/search-profile`;
    return this.http.post(url, criteria);
  }

  updateActivationStatus(id: any, isActive: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/change-activation/${id}/${isActive}`;
    return this.http.post(url, {});
  }

  delete(id: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/${id}`;
    return this.http.delete(url);
  }

  changeEscalation(userId: string, employee: any): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/escalation-change/${userId}`;
    return this.http.post(url, employee);
  }

  getTeamDetails(): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/team-details`;
    return this.http.get(url);
  }

  getTeamDetailsOfUser(userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/team-details/${userId}`;
    return this.http.get(url);
  }

  getLoggedInUsers(): Observable<any> {
    const url = `${environment.apiOrigin}/api/security/logged-in-users`;
    return this.http.get(url);
  }

  sendWelcomeEmail(userId: string): Observable<any> {
    const url = `${environment.apiOrigin}/api/users/send-welcome-email/${userId}`;
    return this.http.post(url, {});
  }
}
